/**
 * 资源 URL 解析工具
 * 
 * 【架构规则】
 * - 所有从 VideoDescriptor.assetUrls 获取的 URL 必须通过此工具解析
 * - 后端可能返回相对路径（如 /assets/{videoId}/video）或绝对路径（如 CDN/S3 URL）
 * - 前端负责根据运行时环境解析这些 URL
 * 
 * 规则：
 * - 如果 URL 已经是绝对路径（http:// 或 https://），直接使用
 * - 如果 URL 是相对路径，添加 ASSET_BASE_URL 前缀
 *   - 开发环境：ASSET_BASE_URL = "http://localhost:3001" → 生成绝对 URL
 *   - 生产环境：ASSET_BASE_URL = "" → 保持相对路径（由 Nginx 处理）
 */

import { ASSET_BASE_URL } from '../config/api';

/**
 * 资源 URL 类型（用于类型断言）
 */
export interface AssetUrl {
  url: string;
}

/**
 * 类型守卫：检查值是否为 AssetUrl 对象
 * 
 * 使用类型守卫确保类型安全，避免在泛型上下文中使用 `'url' in value` 的类型问题
 * 
 * @param value 待检查的值
 * @returns 如果 value 是 AssetUrl 对象则返回 true，TypeScript 会缩小类型
 */
function isAssetUrl(value: unknown): value is AssetUrl {
  return (
    value !== null &&
    typeof value === 'object' &&
    'url' in value &&
    typeof (value as Record<string, unknown>).url === 'string'
  );
}

/**
 * 解析资源 URL
 * 
 * @param url 资源 URL（可能为相对路径或绝对路径）
 * @returns 解析后的 URL，如果输入为 undefined 则返回 undefined
 * 
 * @example
 * // 开发环境：ASSET_BASE_URL = "http://localhost:3001"
 * resolveAssetUrl("/assets/video123/video")
 * // → "http://localhost:3001/assets/video123/video"
 * 
 * // 生产环境：ASSET_BASE_URL = ""
 * resolveAssetUrl("/assets/video123/video")
 * // → "/assets/video123/video"
 * 
 * // 绝对 URL（CDN/S3）直接返回
 * resolveAssetUrl("https://cdn.example.com/video.mp4")
 * // → "https://cdn.example.com/video.mp4"
 */
export function resolveAssetUrl(url?: string): string | undefined {
  if (!url) return undefined;

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return ASSET_BASE_URL ? `${ASSET_BASE_URL}${url}` : url;
}

/**
 * 批量解析资源 URL（类型安全的实现）
 * 
 * 使用类型守卫确保类型安全，避免在泛型上下文中使用 `'url' in value` 的类型问题
 * 
 * @param urls 资源 URL 对象（如 VideoAssets）
 * @returns 解析后的 URL 对象
 */
export function resolveAssetUrls<T extends Record<string, unknown>>(urls: T): T {
  const resolved = { ...urls } as T;

  for (const key in urls) {
    const value = urls[key];
    
    // 使用类型守卫进行安全的类型缩小
    // 类型守卫确保在 if 块内 value 是 AssetUrl 类型
    if (isAssetUrl(value)) {
      resolved[key] = {
        ...value,
        url: resolveAssetUrl(value.url),
      } as T[typeof key];
    }
  }

  return resolved;
}

